import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  UsersIcon, 
  MagnifyingGlassIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  StarIcon,
  BellIcon,
  KeyIcon,
  QrCodeIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  UserIcon,
  DocumentTextIcon,
  EyeIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { CurrencyRupeeIcon } from '@heroicons/react/24/solid'
import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'

function StaffTab({ restaurant }) {
  const [staff, setStaff] = useState([])
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPosition, setFilterPosition] = useState('all')
  const [activeTab, setActiveTab] = useState('staff')
  const [processingId, setProcessingId] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [generatingKey, setGeneratingKey] = useState(false)
  const [showKeyModal, setShowKeyModal] = useState(false)

  useEffect(() => {
    if (restaurant?.id) {
      fetchStaffMembers()
      if (activeTab === 'applications') {
        fetchApplications()
      }
    }
  }, [restaurant?.id, activeTab])

  const fetchStaffMembers = async () => {
    try {
      setLoading(true)
      console.log('🔍 Fetching staff members for restaurant:', restaurant.id)
      
      // Fetch approved staff from unified users table
      const { data: staffData, error } = await supabase
        .from('users')
        .select('*')
        .eq('restaurant_id', restaurant.id)
        .eq('role', 'staff')
        .not('approved_at', 'is', null) // Only approved staff
        .order('created_at', { ascending: false })

      if (error) throw error
      
      console.log('✅ Staff members fetched:', staffData)
      setStaff(staffData || [])
    } catch (error) {
      console.error('❌ Error fetching staff members:', error)
      toast.error('Failed to load staff members')
    } finally {
      setLoading(false)
    }
  }

  const fetchApplications = async () => {
    try {
      setLoading(true)
      
      const { data: applicationsData, error } = await supabase
        .from('staff_applications')
        .select('*')
        .eq('restaurant_id', restaurant.id)
        .order('applied_at', { ascending: false })

      if (error) throw error

      console.log('✅ Applications fetched:', applicationsData)
      setApplications(applicationsData || [])
    } catch (error) {
      console.error('Error fetching applications:', error)
      toast.error('Failed to load staff applications')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveApplication = async (application) => {
    try {
      setProcessingId(application.id)

      // Update application status to approved
      const { error: updateError } = await supabase
        .from('staff_applications')
        .update({ 
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: restaurant.owner_id
        })
        .eq('id', application.id)

      if (updateError) throw updateError

      // Update user record in unified users table with approval data
      const { error: userError } = await supabase
        .from('users')
        .update({
          restaurant_id: restaurant.id,
          position: application.position,
          hourly_rate: application.hourly_rate || 0,
          approved_at: new Date().toISOString(),
          approved_by: restaurant.owner_id
        })
        .eq('id', application.user_id)

      if (userError) throw userError

      toast.success(`✅ ${application.full_name} approved!\n\n🎉 They can now login and access the staff dashboard.`, {
        duration: 6000,
        style: { maxWidth: '400px', whiteSpace: 'pre-line' }
      })

      fetchApplications()
      fetchStaffMembers() // Refresh staff list
    } catch (error) {
      console.error('Error approving application:', error)
      toast.error('Failed to approve application')
    } finally {
      setProcessingId(null)
    }
  }

  const handleRejectApplication = async (application) => {
    try {
      setProcessingId(application.id)

      const { error } = await supabase
        .from('staff_applications')
        .update({ 
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: restaurant.owner_id
        })
        .eq('id', application.id)

      if (error) throw error

      toast.success(`Application from ${application.full_name} rejected`)
      fetchApplications()
    } catch (error) {
      console.error('Error rejecting application:', error)
      toast.error('Failed to reject application')
    } finally {
      setProcessingId(null)
    }
  }

  const generateSignupKey = async () => {
    try {
      setGeneratingKey(true)
      
      const generateKey = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
        let key = ''
        for (let i = 0; i < 12; i++) {
          if (i === 4 || i === 8) key += '-'
          else key += chars.charAt(Math.floor(Math.random() * chars.length))
        }
        return key
      }

      const newKey = generateKey()

      const { error } = await supabase
        .from('users')
        .update({ staff_signup_key: newKey })
        .eq('id', restaurant.id)
        .eq('role', 'restaurant_owner')

      if (error) throw error

      toast.success(`🎉 New signup key generated!\n\n🔑 Key: ${newKey}\n\n📤 Share this with potential staff members to apply.`, {
        duration: 10000,
        style: { 
          maxWidth: '450px', 
          whiteSpace: 'pre-line',
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: 'white',
          fontWeight: '500'
        }
      })

      // Update restaurant object to reflect the new key
      restaurant.staff_signup_key = newKey
      
      // Copy to clipboard automatically
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(newKey)
        setTimeout(() => {
          toast.success('📋 Key copied to clipboard!', { duration: 3000 })
        }, 1000)
      }
    } catch (error) {
      console.error('Error generating signup key:', error)
      toast.error('❌ Failed to generate signup key. Please try again.')
    } finally {
      setGeneratingKey(false)
    }
  }

  const regenerateSignupKey = async () => {
    if (!confirm('🔄 Generate a new signup key?\n\n⚠️ The current key will stop working and staff using it won\'t be able to apply.\n\nContinue?')) {
      return
    }
    await generateSignupKey()
  }

  const refreshData = async () => {
    try {
      setRefreshing(true)
      
      // Refresh both staff and applications data
      await Promise.all([
        fetchStaffMembers(),
        activeTab === 'applications' ? fetchApplications() : Promise.resolve()
      ])
      
      toast.success('🔄 Data refreshed successfully!', {
        duration: 3000,
        style: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white'
        }
      })
    } catch (error) {
      console.error('Error refreshing data:', error)
      toast.error('❌ Failed to refresh data')
    } finally {
      setRefreshing(false)
    }
  }

  // Filter staff based on search and position
  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.phone?.includes(searchTerm)
    const matchesPosition = filterPosition === 'all' || member.position === filterPosition
    return matchesSearch && matchesPosition
  })

  // Get unique positions for filter
  const positions = [...new Set(staff.map(member => member.position).filter(Boolean))]
  
  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.position?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const pendingCount = applications.filter(app => app.status === 'pending').length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      {/* Tab Navigation */}
      <div className="flex bg-gray-50/50 p-1.5 rounded-[24px] border border-gray-100 w-full sm:w-fit">
        <div className="flex space-x-1 w-full">
            <button
            onClick={() => setActiveTab('staff')}
            className={`px-8 py-3 rounded-[18px] text-[10px] font-black uppercase tracking-widest transition-all ${
              activeTab === 'staff'
                ? 'bg-white text-gray-900 shadow-xl shadow-gray-200/50 ring-1 ring-gray-100'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            Staff Team ({staff.length})
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-8 py-3 rounded-[18px] text-[10px] font-black uppercase tracking-widest transition-all relative ${
              activeTab === 'applications'
                ? 'bg-white text-gray-900 shadow-xl shadow-gray-200/50 ring-1 ring-gray-100'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            Hiring
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-[8px] font-black h-5 w-5 rounded-full flex items-center justify-center border-2 border-white shadow-lg animate-pulse">
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Staff Tab Content */}
      {activeTab === 'staff' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8 mb-10">
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight lowercase">
                <span className="text-orange-500 italic">/</span>staff team
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                {staff.length} Active staff members verified
              </p>
            </div>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="flex items-center gap-3 px-6 py-3 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-xl shadow-gray-200 disabled:opacity-50"
            >
              {refreshing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <ArrowPathIcon className="h-4 w-4 stroke-[3]" />
              )}
              {refreshing ? 'Updating...' : 'Refresh List'}
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-6 mb-12">
            <div className="relative flex-1 group">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-5 top-1/2 transform -translate-y-1/2 group-focus-within:text-orange-500 transition-colors" />
              <input
                type="text"
                placeholder="Search staff by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-6 py-4 bg-white border border-gray-100 rounded-[20px] focus:ring-4 focus:ring-orange-500/5 focus:border-orange-200 transition-all shadow-sm font-medium placeholder:text-gray-300 text-sm"
              />
            </div>
            <div className="relative group">
              <FunnelIcon className="h-5 w-5 text-gray-400 absolute left-5 top-1/2 transform -translate-y-1/2 group-focus-within:text-orange-500 transition-colors" />
              <select
                value={filterPosition}
                onChange={(e) => setFilterPosition(e.target.value)}
                className="pl-14 pr-12 py-4 bg-white border border-gray-100 rounded-[20px] focus:ring-4 focus:ring-orange-500/5 focus:border-orange-200 transition-all shadow-sm font-black text-[10px] uppercase tracking-widest appearance-none cursor-pointer"
              >
                <option value="all">Everywhere</option>
                {positions.map(position => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>
            </div>
          </div>

      {/* Staff List */}
      {filteredStaff.length === 0 ? (
        <div className="text-center py-12">
          <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {staff.length === 0 ? 'No Staff Members Yet' : 'No Staff Found'}
          </h3>
          <p className="text-gray-600">
            {staff.length === 0 
              ? 'Staff members will appear here once they are approved through the Applications tab.'
              : 'Try adjusting your search or filter criteria.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredStaff.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05, type: "spring" }}
                className="bg-white rounded-[40px] border border-gray-100 p-8 hover:shadow-2xl hover:shadow-gray-200/20 transition-all duration-700 group"
              >
                {/* Staff Avatar and Name */}
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center border-2 border-white ring-4 ring-gray-50 group-hover:ring-orange-100 transition-all overflow-hidden relative">
                    <span className="text-xl font-black text-gray-400 uppercase italic group-hover:text-orange-500 transition-colors">
                      {member.full_name?.charAt(0) || 'S'}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/0 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-gray-900 group-hover:text-orange-600 transition-colors uppercase tracking-tight">
                      {member.full_name || 'Anonymous Staff'}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        {member.position || 'Staff Member'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/50">
                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-sm">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <span className="text-xs font-bold text-gray-600 truncate">
                      {member.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/50">
                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-sm">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <span className="text-xs font-bold text-gray-600">
                      {member.phone || 'No Phone Number'}
                    </span>
                  </div>
                </div>

                {/* Performance & Meta */}
                <div className="pt-8 border-t border-gray-50 grid grid-cols-2 gap-6">
                  <div className="text-center bg-gray-50/30 rounded-[24px] p-4 border border-gray-100 transition-all hover:bg-white hover:shadow-lg hover:shadow-gray-200/20">
                    <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">Orders</p>
                    <p className="text-xl font-black text-gray-900 leading-none">
                      {member.total_orders_completed || 0} <span className="text-[10px] text-gray-400 font-bold">Total</span>
                    </p>
                  </div>
                  <div className="text-center bg-gray-50/30 rounded-[24px] p-4 border border-gray-100 transition-all hover:bg-white hover:shadow-lg hover:shadow-gray-200/20">
                    <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">Score</p>
                    <div className="flex items-center justify-center gap-1.5">
                      <StarIcon className="h-4 w-4 text-orange-500 fill-orange-500" />
                      <p className="text-xl font-black text-gray-900 leading-none">
                        {member.performance_rating || '5.0'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-between items-center group/footer">
                   <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${member.is_available ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`}></div>
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                         {member.is_available ? 'On Duty' : 'Off Duty'}
                      </span>
                   </div>
                   <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                      <span className="text-[9px] font-black text-orange-600 uppercase tracking-widest">View Profile</span>
                      <UserIcon className="h-3 w-3 text-orange-600" />
                   </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
        </>
      )}

      {/* Applications Tab Content */}
      {activeTab === 'applications' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8 mb-10">
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight lowercase">
                <span className="text-orange-500 italic">/</span>hiring
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                {applications.length} Pending staff applications
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={refreshData}
                disabled={refreshing}
                className="flex items-center gap-3 px-6 py-3 bg-gray-50 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-100 transition-all border border-gray-100 disabled:opacity-50"
              >
                {refreshing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                ) : (
                  <ArrowPathIcon className="h-4 w-4 stroke-[3]" />
                )}
                {refreshing ? 'Updating...' : 'Refresh List'}
              </button>
              
              <button
                onClick={restaurant?.staff_signup_key ? regenerateSignupKey : generateSignupKey}
                disabled={generatingKey}
                className="flex items-center gap-3 px-6 py-3 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-xl shadow-gray-200 disabled:opacity-50"
              >
                {generatingKey ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <KeyIcon className="h-4 w-4 stroke-[3]" />
                )}
                {generatingKey ? 'Creating...' : restaurant?.staff_signup_key ? 'Reset Link' : 'Create Link'}
              </button>
            </div>
          </div>

          {/* Enhanced Signup Key Display */}
          {restaurant?.staff_signup_key && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100 rounded-[32px] p-10 shadow-xl shadow-gray-100/50 mb-12 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-10 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                <QrCodeIcon className="h-32 w-32 text-gray-900" />
              </div>
              
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-emerald-50 text-emerald-600 text-[8px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest border border-emerald-100">Link is Live</span>
                    <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Secure Staff Signup</span>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight mb-4">Staff Signup Code</h3>
                  <p className="text-xs text-gray-500 font-medium leading-relaxed max-w-md">
                    Share this code with candidates to let them apply. Approved staff will automatically appear in your team list.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl px-8 py-5 group-hover:bg-white group-hover:shadow-lg transition-all">
                    <code className="text-2xl font-black text-gray-900 tracking-[0.3em]">
                      {restaurant.staff_signup_key}
                    </code>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(restaurant.staff_signup_key)
                      toast.success('📋 Encrypted key copied to clipboard')
                    }}
                    className="flex lg:flex-col items-center gap-2 px-8 py-5 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 shadow-xl shadow-orange-100 transition-all group/btn"
                  >
                    <DocumentTextIcon className="h-5 w-5 stroke-[3] group-hover/btn:scale-110 transition-transform" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Copy Code</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Search */}
          <div className="relative group mb-12">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-5 top-1/2 transform -translate-y-1/2 group-focus-within:text-orange-500 transition-colors" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-white border border-gray-100 rounded-[20px] focus:ring-4 focus:ring-orange-500/5 focus:border-orange-200 transition-all shadow-sm font-medium placeholder:text-gray-300 text-sm"
            />
          </div>

          {/* Applications List */}
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {applications.length === 0 ? 'No Applications Yet' : 'No Applications Found'}
              </h3>
              <p className="text-gray-600">
                {applications.length === 0 
                  ? 'Staff applications will appear here when people apply using your signup key.'
                  : 'Try adjusting your search criteria.'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8">
              <AnimatePresence>
                {filteredApplications.map((application, index) => (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05, type: "spring" }}
                    className="bg-white rounded-[40px] border border-gray-100 p-10 hover:shadow-2xl hover:shadow-gray-200/20 transition-all duration-700 group"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                      <div className="flex-1">
                        <div className="flex items-center gap-6 mb-8">
                          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center border-2 border-white ring-4 ring-gray-50 group-hover:ring-orange-100 transition-all overflow-hidden relative">
                            <span className="text-xl font-black text-gray-400 uppercase italic">
                              {application.full_name?.charAt(0) || 'A'}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-xl font-black text-gray-900 group-hover:text-orange-600 transition-colors uppercase tracking-tight">
                               {application.full_name}
                            </h3>
                            <div className="flex items-center gap-4 mt-1">
                               <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">{application.position} Candidate</span>
                               <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                               <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">₹{application.hourly_rate}/hr Req.</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                          <div className="flex items-center gap-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-100/50">
                            <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-xs font-bold text-gray-600">{application.email}</span>
                          </div>
                          <div className="flex items-center gap-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-100/50">
                            <PhoneIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-xs font-bold text-gray-600">{application.phone || 'No Signal'}</span>
                          </div>
                        </div>

                        {application.message && (
                          <div className="relative p-6 bg-orange-50/30 rounded-[28px] border border-orange-100/50 mb-8 italic text-sm text-gray-600 font-medium">
                            "{application.message}"
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-center lg:items-end gap-6 min-w-fit">
                        <div className="text-center lg:text-right">
                           <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                             application.status === 'pending' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                             application.status === 'approved' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                             'bg-rose-50 text-rose-600 border-rose-100'
                           }`}>
                             {application.status}
                           </span>
                           <p className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em] mt-3 italic">
                             Applied On: {new Date(application.applied_at).toLocaleDateString()}
                           </p>
                        </div>

                        {application.status === 'pending' && (
                          <div className="flex gap-4">
                            <button
                              onClick={() => handleApproveApplication(application)}
                              disabled={processingId === application.id}
                              className="px-8 py-4 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-black shadow-xl shadow-gray-200 transition-all disabled:opacity-50 group/btn"
                            >
                               {processingId === application.id ? 'Approving...' : 'Approve Staff'}
                            </button>
                            <button
                              onClick={() => handleRejectApplication(application)}
                              disabled={processingId === application.id}
                              className="px-8 py-4 bg-white border border-gray-200 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-gray-50 transition-all disabled:opacity-50"
                            >
                               {processingId === application.id ? 'Rejecting...' : 'Reject Application'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default StaffTab
